import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { AlertCircle, CheckCircle2, Trophy, ArrowRight } from 'lucide-react';
import { validateProfileCompletion, getFieldLabel } from '@/services/profileValidationService';

interface ProfileCompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceed: () => void;
  userProfile: any;
  userRole: string;
}

export const ProfileCompletionModal: React.FC<ProfileCompletionModalProps> = ({
  isOpen,
  onClose,
  onProceed,
  userProfile,
  userRole,
}) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const validationResult = validateProfileCompletion(userProfile, userRole);

  useEffect(() => {
    // Check if user has previously opted to not show this modal
    const hideModal = localStorage.getItem('hideProfileCompletionModal');
    if (hideModal === 'true' && validationResult.isComplete) {
      onClose();
    }
  }, [validationResult.isComplete, onClose]);

  const handleProceed = () => {
    if (dontShowAgain) {
      localStorage.setItem('hideProfileCompletionModal', 'true');
    }
    onProceed();
  };

  const handleSkip = () => {
    if (dontShowAgain) {
      localStorage.setItem('hideProfileCompletionModal', 'true');
    }
    onClose();
  };

  if (validationResult.isComplete) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] bg-gray-900 border-gray-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl text-white">
            <Trophy className="h-6 w-6 text-sports-green" />
            Complete Your Profile & Earn 50 Points!
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            You're {validationResult.completionPercentage}% done! Fill in the missing fields to unlock your bonus.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 my-4">
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-300">Profile Completion</span>
              <span className="text-sports-green font-semibold">{validationResult.completionPercentage}%</span>
            </div>
            <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-blue-500 to-sports-green transition-all duration-500"
                style={{ width: `${validationResult.completionPercentage}%` }}
              />
            </div>
          </div>

          {/* Required Fields */}
          {validationResult.missingRequiredFields.length > 0 && (
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <h3 className="font-semibold text-red-300">Required Fields ({validationResult.missingRequiredFields.length})</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {validationResult.missingRequiredFields.map((field) => (
                  <div key={field} className="flex items-center gap-2 text-sm">
                    <span className="text-red-400">●</span>
                    <span className="text-gray-300">{getFieldLabel(field)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommended Fields */}
          {validationResult.missingRecommendedFields && validationResult.missingRecommendedFields.length > 0 && (
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="h-5 w-5 text-blue-400" />
                <h3 className="font-semibold text-blue-300">Recommended Fields (Optional)</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {validationResult.missingRecommendedFields.slice(0, 6).map((field) => (
                  <div key={field} className="flex items-center gap-2 text-sm">
                    <span className="text-yellow-400">○</span>
                    <span className="text-gray-400">{getFieldLabel(field)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Message */}
          <div className="bg-blue-950/30 border border-blue-500/20 rounded-lg p-3">
            <p className="text-sm text-blue-200">{validationResult.roleSpecificMessage}</p>
          </div>

          {/* Don't Show Again */}
          <div className="flex items-center space-x-2 pt-2">
            <Checkbox
              id="dontShowAgain"
              checked={dontShowAgain}
              onCheckedChange={(checked) => setDontShowAgain(checked as boolean)}
              className="border-gray-600"
            />
            <Label htmlFor="dontShowAgain" className="text-sm text-gray-400 cursor-pointer">
              Don't show this reminder again
            </Label>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleSkip}
            className="border-gray-600 text-gray-400 hover:bg-gray-800"
          >
            Skip for Now
          </Button>
          <Button
            onClick={handleProceed}
            className="bg-sports-green text-black hover:bg-green-600"
          >
            Let's Complete My Profile
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
